import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'
import { Calendar } from 'blaze-ui/icons/32'
import { ImproveQuality } from 'blaze-ui/icons/36'
import { HighDetailedEngagement, Logout1 } from 'blaze-ui/icons'
import { Lightning, AlertTriangle, Bag04 } from 'blaze-ui/icons/20'

import { useSession } from 'hooks/useSession'
import { useEnforceBlazeFreeTrial } from 'hooks/useEnforceBlazeFreeTrial'
import { WORKSPACE_SELECTION_PATH } from 'paths'

import Styles from './NewPaywallModal.module.scss'
import { useCreateAutopilotWorkspace } from 'components/Settings/Workspace/VirtualMarketerUpgradeCta/hooks/useCreateAutopilotWorkspace'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { noop } from 'lodash'
import useResponsiveDesign from 'hooks/useResponsiveDesign'

const INTERCOM_LIVE_DEMO_MESSAGE = "Hello! I'd like to schedule a live demo to see Blaze Autopilot in action."

const LightningIcon = () => <Lightning color="white" size={24} />

interface Props {
  logoutOverride?: () => void
}

export const NewPaywallModal = ({ logoutOverride }: Props) => {
  const { closeModal } = useModals()
  const { logout } = useSession()
  const { hasOtherPayingWorkspaces } = useEnforceBlazeFreeTrial()
  const { createAutopilotWorkspace } = useCreateAutopilotWorkspace()
  const { isMobile } = useResponsiveDesign()

  const handleStartFreeTrial = () => {
    closeModal()
    createAutopilotWorkspace()
  }

  const handleGetLiveDemo = () => {
    const isIntercomAvailable = typeof window !== 'undefined' && typeof window.Intercom === 'function'

    if (isIntercomAvailable) {
      window.Intercom('showNewMessage', INTERCOM_LIVE_DEMO_MESSAGE)
    } else {
      window.open('https://calendly.com/blaze-demo', '_blank')
    }
  }

  const handleLogout = () => {
    closeModal()
    if (logoutOverride) {
      logoutOverride()
    } else {
      logout()
    }
  }

  return (
    <Modal.Root bodyClassName={Styles.modalBody} onPressOutside={noop}>
      <Modal.Header className={Styles.modalHeader} preventClose>
        <div className={Styles.paywallHeader}>
          <div className={Styles.leftControls}>
            {hasOtherPayingWorkspaces && (
              <ButtonLink variant="ghost" size="sm" to={WORKSPACE_SELECTION_PATH}>
                Switch Workspaces
              </ButtonLink>
            )}
          </div>
          <div className={Styles.rightControls}>
            <Button variant="ghost" size="sm" onPress={handleLogout} frontIcon={Logout1}>
              Log Out
            </Button>
          </div>
        </div>
      </Modal.Header>

      <Modal.Content className={Styles.modalContent}>
        <div className={Styles.mainContainer}>
          <div className={Styles.contentConstraint}>
            <div className={Styles.contentWrapper}>
              <div className={Styles.headerSection}>
                <div className={Styles.titleSection}>
                  <Heading level={1}>Introducing Blaze Autopilot</Heading>
                  <div className={Styles.subtitle}>
                    <Text>
                      Blaze is evolving into <strong>Blaze Autopilot</strong> – the next generation of our platform,
                      built to run your marketing automatically. Take a look at what Autopilot can do for your business:
                    </Text>
                  </div>
                </div>

                <ResponsiveDesign.Mobile>
                  <div className={Styles.ctaSection}>
                    <Button
                      size="lg"
                      variant="primary"
                      onPress={handleStartFreeTrial}
                      className={Styles.primaryButton}
                      frontIcon={LightningIcon}
                      fullWidth={isMobile}
                    >
                      Start 7-Day Free Trial
                    </Button>
                    <div className={Styles.ctaSubtext}>100% free. No card needed. Cancel anytime.</div>
                  </div>
                </ResponsiveDesign.Mobile>

                <div className={Styles.featureCards}>
                  <div className={Styles.featureCard}>
                    <Calendar className={Styles.featureIcon} />
                    <div className={Styles.featureText}>
                      <Text>Create, schedule, and post content based on your content plan – automatically</Text>
                    </div>
                  </div>

                  <div className={Styles.featureCard}>
                    <HighDetailedEngagement size={30} className={Styles.featureIcon} />
                    <div className={Styles.featureText}>
                      <Text>Deliver your content in weekly batches for quick review and simple editing</Text>
                    </div>
                  </div>

                  <div className={Styles.featureCard}>
                    <ImproveQuality className={Styles.featureIcon} />
                    <div className={Styles.featureText}>
                      <Text>Learn from every post so your business grows faster and attracts more leads</Text>
                    </div>
                  </div>
                </div>
              </div>

              <div className={Styles.warningSection}>
                <AlertTriangle size={38} color="var(--dark-80)" />
                <div className={Styles.warningText}>
                  The previous version has been retired. Your old workspace and content are no longer accessible.
                  Upgrade to Autopilot to keep growing with Blaze.
                </div>
              </div>

              <ResponsiveDesign.Desktop>
                <div className={Styles.ctaSection}>
                  <Button
                    size="lg"
                    variant="primary"
                    onPress={handleStartFreeTrial}
                    className={Styles.primaryButton}
                    frontIcon={LightningIcon}
                  >
                    Start 7-Day Free Trial
                  </Button>
                  <div className={Styles.ctaSubtext}>100% free. No card needed. Cancel anytime.</div>
                </div>
              </ResponsiveDesign.Desktop>
            </div>
          </div>

          <ResponsiveDesign.Mobile>
            <div className={Styles.mobileBottomCta}>
              <Button
                size="lg"
                variant="primary"
                onPress={handleStartFreeTrial}
                className={Styles.primaryButton}
                frontIcon={LightningIcon}
                fullWidth={isMobile}
              >
                Start 7-Day Free Trial
              </Button>
            </div>
          </ResponsiveDesign.Mobile>

          <div className={Styles.demoSection}>
            <div className={Styles.demoPrompt}>Want to see it in action?</div>
            <div className={Styles.demoButton} onClick={handleGetLiveDemo}>
              <div className={Styles.demoButtonContent}>
                <div className={Styles.avatarContainer}>
                  <img
                    className={Styles.avatar}
                    src="https://res.cloudinary.com/almanac/image/upload/v1754670425/onboarding_assets/sider_component/support_avatar_cbq4wg.png"
                    alt="Demo Avatar"
                  />
                </div>
                <span className={Styles.demoButtonText}>Get Live Demo</span>
              </div>
            </div>
          </div>
          <div className={Styles.loyaltyBanner}>
            <Bag04 size={24} color="var(--Magenta-70, #7F24B7)" />
            <div className={Styles.loyaltyText}>
              Loyalty bonus: Enjoy special pricing as our thank-you when your trial ends!
            </div>
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}
